import { MeetApiError } from '@/lib/api';

/**
 * A 401 from any admin endpoint means the session cookie expired or was
 * revoked. Callers send the user back to the login page rather than showing an
 * error, because there is nothing to retry until they sign in again.
 */
export function isSessionExpired(error: unknown): boolean {
  return error instanceof MeetApiError && error.status === 401;
}

export function isNotFound(error: unknown): boolean {
  return error instanceof MeetApiError && error.status === 404;
}

/** Server messages are written for recruiters, so they are surfaced verbatim. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof MeetApiError && error.message ? error.message : fallback;
}

export const SESSION_TIMEOUT_MS = 10_000;
export const REQUEST_TIMEOUT_MS = 20_000;

/**
 * `fetch` only rejects when a connection fails outright. A wedged API or a
 * proxy that accepts the socket and never answers leaves it pending forever,
 * which in the console means a spinner that never resolves into anything the
 * admin can act on. A deadline turns that into a retryable error.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new MeetApiError(0, 'timeout', message)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}
